import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import FieldHint from "../components/FieldHint";
import {
  api,
  ApiError,
  type RankingStandingRow,
  type RankingSystemEvent,
  type RankingSystemSummary,
} from "../lib/api";
import {
  BONUS_LABELS,
  DEFAULT_CUSTOM_RULES,
  GROUP_BONUS_SIZES,
  groupBonusValue,
  withGroupBonus,
  INITIAL_POINTS_LAYOUT,
  OPTIONAL_RULE_LABELS,
  RULE_LABELS,
  SCORE_RULE_LABELS,
  TIEBREAKER_LABELS,
  parseInitialPointsRows,
  withRuleDefaults,
  type CustomRankingRules,
  type DecimalRuleKey,
  type IntRuleKey,
  type OptionalRuleKey,
  type Tiebreaker,
} from "../lib/customRanking";
import { formatDateTime } from "../lib/dateFormat";
import { parseCsv } from "./AdminRankingHubPage";

/** "Mi ranking" (Eduardo 2026-09-22): el organizador crea y administra su
 * propio sistema de puntos. Sin id = crear; con id = editar, cargar puntos
 * iniciales, ver el ranking vivo, publicarlo y revisar de dónde salió cada
 * punto. Los torneos se ligan desde el paso 7 del wizard. */

interface CategoryDraft {
  id?: number;
  name: string;
  best_n: number;
}

const KIND_LABELS: Record<string, string> = {
  match_win: "Partido ganado",
  match_loss: "Partido perdido",
  round_points: "Bono por posición",
  initial_points: "Puntos iniciales",
  sanction: "Sanción",
  manual_adjust: "Ajuste manual",
};

export default function AdminCustomRankingPage() {
  const { systemId } = useParams<{ systemId: string }>();
  const editingId = systemId ? Number(systemId) : null;
  const { token } = useAuth();
  const navigate = useNavigate();

  const [system, setSystem] = useState<RankingSystemSummary | null>(null);
  const [name, setName] = useState("");
  const [seasonStart, setSeasonStart] = useState("");
  const [seasonEnd, setSeasonEnd] = useState("");
  const [rules, setRules] = useState<CustomRankingRules>(DEFAULT_CUSTOM_RULES);
  const [categories, setCategories] = useState<CategoryDraft[]>([{ name: "General", best_n: 0 }]);
  // Eduardo 2026-09-23: "simple" = victoria + campeón + finalista en 30
  // segundos; "avanzado" muestra todas las reglas. Es solo presentación: las
  // reglas ocultas conservan su valor.
  const [mode, setMode] = useState<"simple" | "advanced">("simple");
  // Torneos contabilizados: todos, o los mejores N (aplica a todas las categorías).
  const [countMode, setCountMode] = useState<"all" | "best">("all");
  const [bestN, setBestN] = useState(5);
  const [loading, setLoading] = useState(Boolean(editingId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Operación (solo al editar)
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [standings, setStandings] = useState<RankingStandingRow[]>([]);
  const [events, setEvents] = useState<RankingSystemEvent[]>([]);
  const [initialCsv, setInitialCsv] = useState("");
  const [unmatched, setUnmatched] = useState<string[]>([]);
  // Penalizaciones (sanción con motivo) desde la pantalla.
  const [penaltyPlayerId, setPenaltyPlayerId] = useState("");
  const [penaltyPoints, setPenaltyPoints] = useState("5");
  const [penaltyReason, setPenaltyReason] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);

  const applySystem = useCallback((item: RankingSystemSummary) => {
    setSystem(item);
    setName(item.name);
    setSeasonStart(item.season_start ?? "");
    setSeasonEnd(item.season_end ?? "");
    setRules(withRuleDefaults(item.rules as Partial<CustomRankingRules> | null));
    setCategories(item.categories.map((c) => ({ id: c.id, name: c.name, best_n: c.best_n })));
    setSelectedCategoryId((current) => current ?? item.categories[0]?.id ?? null);
    const rulesOf = withRuleDefaults(item.rules as Partial<CustomRankingRules> | null);
    const advanced =
      rulesOf.match_loss !== 0 ||
      rulesOf.walkover_win !== rulesOf.match_win ||
      rulesOf.walkover_loss !== rulesOf.match_loss ||
      rulesOf.bonus.sf !== 0 || rulesOf.bonus.qf !== 0 || rulesOf.bonus.r16 !== 0 || rulesOf.bonus.r32 !== 0 ||
      rulesOf.set_win !== 0 || rulesOf.set_loss !== 0 || rulesOf.game_win !== 0 || rulesOf.game_loss !== 0 ||
      rulesOf.participation_bonus !== 0 || rulesOf.undefeated_bonus !== 0 ||
      rulesOf.retired_win !== null || rulesOf.retired_loss !== null || rulesOf.defaulted_win !== null || rulesOf.defaulted_loss !== null;
    setMode(advanced ? "advanced" : "simple");
    const maxBest = Math.max(0, ...item.categories.map((c) => c.best_n));
    setCountMode(maxBest > 0 ? "best" : "all");
    if (maxBest > 0) setBestN(maxBest);
  }, []);

  const load = useCallback(async () => {
    if (!token || !editingId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.rankings.listSystems(token);
      const found = response.data.systems.find((item) => item.id === editingId);
      if (!found) {
        setError("No encontramos este ranking (o pertenece a otro organizador).");
      } else if (!found.is_custom) {
        setError("Este sistema no es un ranking propio: se administra desde Sistemas de ranking.");
      } else {
        applySystem(found);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setLoading(false);
    }
  }, [token, editingId, applySystem]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadOperation = useCallback(async () => {
    if (!token || !editingId || !selectedCategoryId) {
      setStandings([]);
      setEvents([]);
      return;
    }
    try {
      const [standingsResp, eventsResp] = await Promise.all([
        api.rankings.standings(token, editingId, selectedCategoryId),
        api.rankings.systemEvents(token, editingId, selectedCategoryId),
      ]);
      setStandings(standingsResp.data.standings);
      setEvents(eventsResp.data.events);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    }
  }, [token, editingId, selectedCategoryId]);

  useEffect(() => {
    void loadOperation();
  }, [loadOperation]);

  const selectedCategory = useMemo(
    () => system?.categories.find((c) => c.id === selectedCategoryId) ?? null,
    [system, selectedCategoryId],
  );

  function setRule(key: IntRuleKey, value: string) {
    setRules({ ...rules, [key]: Math.round(Number(value) || 0) } as CustomRankingRules);
  }

  function setDecimalRule(key: DecimalRuleKey, value: string) {
    setRules({ ...rules, [key]: Math.round((Number(value) || 0) * 100) / 100 } as CustomRankingRules);
  }

  function setOptionalRule(key: OptionalRuleKey, value: number | null) {
    setRules({ ...rules, [key]: value } as CustomRankingRules);
  }

  function moveTiebreaker(index: number, direction: -1 | 1) {
    const next = [...rules.tiebreakers];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setRules({ ...rules, tiebreakers: next });
  }

  function setBonus(key: keyof CustomRankingRules["bonus"], value: string) {
    setRules({
      ...rules,
      bonus: { ...rules.bonus, [key]: Math.max(Number(value) || 0, 0) } as CustomRankingRules["bonus"],
    });
  }

  function updateCategory(index: number, patch: Partial<CategoryDraft>) {
    setCategories(categories.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  }

  function removeCategory(index: number) {
    // Solo las que aún no existen en el servidor: las guardadas conservan sus puntos.
    if (categories[index].id) return;
    setCategories(categories.filter((_, i) => i !== index));
  }

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    const cleanCategories = categories.filter((c) => c.name.trim());
    if (!name.trim()) {
      setError("Ponle nombre a tu ranking.");
      return;
    }
    if (cleanCategories.length === 0) {
      setError("Agrega al menos una categoría (por ejemplo «General»).");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    // Modo simple: el W.O. vale lo mismo que un partido jugado y no hay bonos
    // más allá de campeón/finalista. Lo demás se conserva tal cual.
    const effectiveRules: CustomRankingRules =
      mode === "simple"
        ? {
            ...rules,
            walkover_win: rules.match_win,
            walkover_loss: rules.match_loss,
            retired_win: null, retired_loss: null, defaulted_win: null, defaulted_loss: null,
            set_win: 0, set_loss: 0, game_win: 0, game_loss: 0,
            participation_bonus: 0, undefeated_bonus: 0,
            bonus: { ...rules.bonus, sf: 0, qf: 0, r16: 0, r32: 0 },
          }
        : rules;
    const effectiveBestN = countMode === "best" ? Math.max(bestN, 1) : 0;
    try {
      if (editingId) {
        const response = await api.rankings.updateCustomSystem(token, editingId, {
          name: name.trim(),
          rules: effectiveRules,
          categories: cleanCategories.map((c) => ({ id: c.id, name: c.name.trim(), best_n: effectiveBestN })),
          season_start: seasonStart || null,
          season_end: seasonEnd || null,
        });
        applySystem(response.data.system);
        setMessage(
          response.data.recomputed_tournaments > 0
            ? `Guardado. Se recalcularon ${response.data.recomputed_tournaments} torneo(s) con las reglas nuevas.`
            : "Guardado.",
        );
        await loadOperation();
      } else {
        const response = await api.rankings.createCustomSystem(token, {
          name: name.trim(),
          rules: effectiveRules,
          categories: cleanCategories.map((c) => ({ name: c.name.trim(), best_n: effectiveBestN })),
          season_start: seasonStart || null,
          season_end: seasonEnd || null,
        });
        navigate(`/admin/rankings/propio/${response.data.system.id}`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleActive() {
    if (!token || !editingId || !system) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.rankings.updateCustomSystem(token, editingId, { is_active: !system.is_active });
      applySystem(response.data.system);
      setMessage(response.data.system.is_active ? "Ranking activo otra vez." : "Ranking pausado: no se ligan torneos ni se suman puntos hasta reactivarlo.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function readCsvFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (/\.xlsx?$/i.test(file.name)) {
      setError("Guarda el archivo de Excel como CSV primero (Archivo → Guardar como → CSV).");
      return;
    }
    try {
      setInitialCsv(await file.text());
      setError(null);
      setMessage(`Archivo "${file.name}" cargado. Revisa y presiona «Cargar puntos iniciales».`);
    } catch {
      setError(`No pudimos leer el archivo "${file.name}".`);
    }
  }

  function downloadLayout() {
    const blob = new Blob([`﻿${INITIAL_POINTS_LAYOUT}\nAna López,ana@correo.mx,5512345678,120\n`], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "puntos-iniciales.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function handleImportInitial() {
    if (!token || !editingId || !selectedCategoryId) return;
    const rows = parseInitialPointsRows(parseCsv(initialCsv));
    if (rows.length === 0) {
      setError(`El CSV está vacío o sin encabezados. Formato esperado: ${INITIAL_POINTS_LAYOUT}`);
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    setUnmatched([]);
    try {
      const response = await api.rankings.importInitialPoints(token, editingId, {
        age_category_id: selectedCategoryId,
        rows,
        replace: true,
      });
      setMessage(`${response.data.matched.length} jugador(es) con puntos iniciales en ${selectedCategory?.name ?? "la categoría"}.`);
      setUnmatched(response.data.unmatched);
      setInitialCsv("");
      await loadOperation();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!token || !editingId || !selectedCategoryId) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.rankings.publish(token, editingId, { age_category_id: selectedCategoryId });
      setMessage(`Ranking publicado con ${response.data.rows.length} jugador(es). Ya se ve en la página pública.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function handlePenalty() {
    if (!token || !editingId || !selectedCategoryId || !penaltyPlayerId) return;
    const points = -Math.abs(Math.round(Number(penaltyPoints) || 0));
    if (points === 0) {
      setError("La penalización debe restar al menos 1 punto.");
      return;
    }
    if (penaltyReason.trim().length < 3) {
      setError("Escribe el motivo de la penalización.");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.rankings.sanction(token, editingId, {
        age_category_id: selectedCategoryId,
        player_profile_id: Number(penaltyPlayerId),
        points,
        reason: penaltyReason.trim(),
      });
      setMessage(`Penalización aplicada (${points} puntos).`);
      setPenaltyReason("");
      await loadOperation();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRecompute() {
    if (!token || !editingId) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const response = await api.rankings.recomputeSystem(token, editingId);
      setMessage(`Se recalcularon ${response.data.recomputed_tournaments} torneo(s).`);
      await loadOperation();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <section className="adm-players__panel adm-rank__panel">
        <p className="muted">Cargando…</p>
      </section>
    );
  }

  return (
    <div className="adm-rank">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Mi ranking</span>
          <h1 className="adm-players__title">
            {editingId ? system?.name ?? "Ranking" : "Crear mi ranking"}
            {system ? (
              <span className={`adm-rank__chip adm-rank__chip--title${system.is_active ? " adm-rank__chip--on" : ""}`}>{system.is_active ? "Activo" : "Pausado"}</span>
            ) : null}
          </h1>
          <p className="adm-rank__lead">
            Tu propio sistema de puntos. Liga tus torneos desde el paso 7 del wizard («Ranking») y cada
            resultado capturado suma o resta puntos solo.
          </p>
        </div>
        <div className="adm-players__head-actions">
          <Link className="adm-players__btn adm-players__btn--secondary" to="/admin/rankings">← Ranking</Link>
          {system ? (
            <Link className="adm-players__btn adm-players__btn--secondary" to={`/rankings/${system.slug}`}>Ver página pública</Link>
          ) : null}
        </div>
      </header>
      {error ? <p role="alert" className="form-error">{error}</p> : null}
      {message ? <p className="form-message">{message}</p> : null}
      {system && !system.is_active ? (
        <p className="alert alert--warning" data-no-toast>Este ranking está pausado: no se ligan torneos ni se suman puntos.</p>
      ) : null}

      <form className="adm-players__panel adm-rank__panel custom-ranking" onSubmit={handleSave}>
        <h2 className="adm-rank__h2">1 · Datos</h2>
        <div className="form">
          <div className="custom-ranking__grid">
            <label>
              Nombre
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ranking Club Norte 2026" required disabled={busy} />
            </label>
            <label>
              Temporada desde
              <input type="date" value={seasonStart} onChange={(e) => setSeasonStart(e.target.value)} disabled={busy} />
            </label>
            <label>
              Temporada hasta
              <input type="date" value={seasonEnd} onChange={(e) => setSeasonEnd(e.target.value)} disabled={busy} />
            </label>
          </div>

          <div className="aviso-radios adm-rank__modes">
            <label className={`aviso-radio${mode === "simple" ? " is-activa" : ""}`}>
              <input type="radio" name="modo-ranking" checked={mode === "simple"} onChange={() => setMode("simple")} disabled={busy} />
              <strong>Ranking simple</strong>
              <small>Partido ganado + campeón y finalista. Listo en 30 segundos.</small>
            </label>
            <label className={`aviso-radio${mode === "advanced" ? " is-activa" : ""}`}>
              <input type="radio" name="modo-ranking" checked={mode === "advanced"} onChange={() => setMode("advanced")} disabled={busy} />
              <strong>Ranking avanzado</strong>
              <small>Derrota que resta, W.O. aparte, bonos por cada ronda y mejores N torneos.</small>
            </label>
          </div>

          <h3 className="custom-ranking__subtitle">
            <span className="hint-label">
              2 · Puntos por partido
              <FieldHint>Aplican a cada partido de cualquier formato: Round Robin, Escalera o eliminatoria. La derrota puede ser negativa para que baje puntos.</FieldHint>
            </span>
          </h3>
          <div className="custom-ranking__rows">
            {RULE_LABELS.filter(([key]) => mode === "advanced" || key === "match_win").map(([key, label, hint]) => (
              <label key={key} className="custom-ranking__row">
                <span>
                  {label}
                  <small className="muted">{hint}</small>
                </span>
                <input type="number" step={1} value={rules[key]} onChange={(e) => setRule(key, e.target.value)} disabled={busy} />
              </label>
            ))}
            {mode === "advanced" ? SCORE_RULE_LABELS.map(([key, label, hint]) => (
              <label key={key} className="custom-ranking__row">
                <span>
                  {label}
                  <small className="muted">{hint}</small>
                </span>
                <input type="number" step={0.1} value={rules[key]} onChange={(e) => setDecimalRule(key, e.target.value)} disabled={busy} />
              </label>
            )) : null}
            {mode === "simple" ? (
              <p className="muted">En modo simple, un W.O. vale lo mismo que un partido jugado y la derrota no resta.</p>
            ) : null}
          </div>

          {mode === "advanced" ? (
            <>
              <h3 className="custom-ranking__subtitle">
                <span className="hint-label">
                  3 · Tie-breaks
                  <FieldHint>Cómo cuentan los tie-breaks al sumar games y sets. Un set 7-6 son 6 games cada uno más lo que valga el tie-break para quien lo ganó.</FieldHint>
                </span>
              </h3>
              <div className="custom-ranking__rows">
                <label className="custom-ranking__row">
                  <span>
                    Tie-break tradicional cuenta como
                    <small className="muted">games para quien lo gana (7-6 = 6 + este valor).</small>
                  </span>
                  <input type="number" min={0} max={10} step={1} value={rules.tiebreak_games} onChange={(e) => setRule("tiebreak_games", e.target.value)} disabled={busy} />
                </label>
                <div className="custom-ranking__row custom-ranking__row--radios">
                  <span>
                    Match tie-break / super tie-break cuenta como
                    <small className="muted">el tercer set decidido a 10 puntos.</small>
                  </span>
                  <span className="custom-ranking__radio-group">
                    <label>
                      <input type="radio" name="super-tb" checked={rules.super_tiebreak_as === "set"} onChange={() => setRules({ ...rules, super_tiebreak_as: "set" })} disabled={busy} />
                      {" "}1 set
                    </label>
                    <label>
                      <input type="radio" name="super-tb" checked={rules.super_tiebreak_as === "games"} onChange={() => setRules({ ...rules, super_tiebreak_as: "games" })} disabled={busy} />
                      {" "}solo games
                    </label>
                    <label>
                      <input type="number" min={0} max={20} step={1} className="custom-ranking__inline-number" value={rules.super_tiebreak_games} onChange={(e) => setRule("super_tiebreak_games", e.target.value)} disabled={busy} />
                      {" "}game(s) para quien lo gana
                    </label>
                  </span>
                </div>
              </div>
            </>
          ) : null}

          <h3 className="custom-ranking__subtitle">
            <span className="hint-label">
              {mode === "advanced" ? "4" : "3"} · Bonos por posición final
              <FieldHint>Campeón, finalista y rondas: solo en cuadros de eliminatoria (y en los playoffs de Round Robin/Escalera). Lugar en el grupo: en Round Robin, según el tamaño del grupo, y se paga cuando el grupo termina. Todo se suma a los puntos por partido. Deja 0 si no quieres bonos.</FieldHint>
            </span>
          </h3>
          <div className="custom-ranking__rows">
            {BONUS_LABELS.filter(([key]) => mode === "advanced" || key === "champion" || key === "finalist").map(([key, label]) => (
              <label key={key} className="custom-ranking__row">
                <span>{label}</span>
                <input type="number" min={0} step={1} value={rules.bonus[key]} onChange={(e) => setBonus(key, e.target.value)} disabled={busy} />
              </label>
            ))}
          </div>

          {/* Eduardo 2026-09-24: en Round Robin el bono depende del tamaño del grupo. */}
          <p className="custom-ranking__group-bonus-title">
            <strong>Lugar final en el grupo (Round Robin)</strong>
            <span className="muted"> · una fila por tamaño de grupo; se paga cuando el grupo termina.</span>
          </p>
          <div className="responsive-table-wrap">
            <table className="data-table custom-ranking__group-bonus-table">
              <thead>
                <tr>
                  <th>Grupo de</th>
                  {Array.from({ length: GROUP_BONUS_SIZES[GROUP_BONUS_SIZES.length - 1] }, (_, index) => (
                    <th key={index} className="standings-col--num">{index + 1}º</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {GROUP_BONUS_SIZES.map((size) => (
                  <tr key={size}>
                    <th scope="row">{size} jugadores</th>
                    {Array.from({ length: GROUP_BONUS_SIZES[GROUP_BONUS_SIZES.length - 1] }, (_, index) => (
                      <td key={index} className="standings-col--num">
                        {index < size ? (
                          <input
                            type="number"
                            min={0}
                            step={1}
                            aria-label={`Bono ${index + 1}º lugar en grupo de ${size}`}
                            value={groupBonusValue(rules, size, index)}
                            onChange={(e) => setRules(withGroupBonus(rules, size, index, Number(e.target.value)))}
                            disabled={busy}
                          />
                        ) : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {mode === "advanced" ? (
            <>
              <h3 className="custom-ranking__subtitle">
                <span className="hint-label">
                  5 · Torneos contabilizados
                  <FieldHint>Con «Mejores N», a cada jugador solo le cuentan sus N mejores torneos; los puntos iniciales y las penalizaciones se suman aparte. El multiplicador de cada torneo se define al ligarlo en su paso 6.</FieldHint>
                </span>
              </h3>
              <div className="aviso-radios adm-rank__modes">
                <label className={`aviso-radio${countMode === "all" ? " is-activa" : ""}`}>
                  <input type="radio" name="torneos-contados" checked={countMode === "all"} onChange={() => setCountMode("all")} disabled={busy} />
                  <strong>Todos</strong>
                  <small>Suma todos los torneos de la temporada.</small>
                </label>
                <label className={`aviso-radio${countMode === "best" ? " is-activa" : ""}`}>
                  <input type="radio" name="torneos-contados" checked={countMode === "best"} onChange={() => setCountMode("best")} disabled={busy} />
                  <strong>
                    Mejores{" "}
                    <input
                      type="number"
                      min={1}
                      step={1}
                      className="custom-ranking__inline-number"
                      value={bestN}
                      onChange={(e) => { setBestN(Math.max(Number(e.target.value) || 1, 1)); setCountMode("best"); }}
                      disabled={busy}
                    />
                  </strong>
                  <small>Solo cuentan los N torneos con más puntos de cada jugador.</small>
                </label>
              </div>

              <h3 className="custom-ranking__subtitle">
                <span className="hint-label">
                  6 · Desempates
                  <FieldHint>A igualdad de puntos, el orden se decide con estos criterios, en este orden. «Enfrentamiento directo» compara solo a los empatados entre sí.</FieldHint>
                </span>
              </h3>
              <ol className="custom-ranking__tiebreakers">
                {rules.tiebreakers.map((key: Tiebreaker, index: number) => (
                  <li key={key}>
                    <span>{index + 1}. {TIEBREAKER_LABELS[key]}</span>
                    <span className="form__actions">
                      <button type="button" className="btn btn--ghost btn--compact" onClick={() => moveTiebreaker(index, -1)} disabled={busy || index === 0} aria-label="Subir">↑</button>
                      <button type="button" className="btn btn--ghost btn--compact" onClick={() => moveTiebreaker(index, 1)} disabled={busy || index === rules.tiebreakers.length - 1} aria-label="Bajar">↓</button>
                    </span>
                  </li>
                ))}
              </ol>

              <h3 className="custom-ranking__subtitle">
                <span className="hint-label">
                  7 · Opciones avanzadas
                  <FieldHint>Retiro y descalificación valen como un partido jugado salvo que les pongas su propio valor. El bono de participación se da por torneo jugado; el de invicto, por ganar todos los partidos del torneo. Las penalizaciones se aplican por jugador más abajo.</FieldHint>
                </span>
              </h3>
              <div className="custom-ranking__rows">
                {OPTIONAL_RULE_LABELS.map(([key, label, hint]) => (
                  <div key={key} className="custom-ranking__row custom-ranking__row--radios">
                    <span>
                      {label}
                      <small className="muted">{hint}</small>
                    </span>
                    <span className="custom-ranking__radio-group">
                      <label>
                        <input type="checkbox" checked={rules[key] === null} onChange={(e) => setOptionalRule(key, e.target.checked ? null : 0)} disabled={busy} />
                        {" "}como partido jugado
                      </label>
                      {rules[key] !== null ? (
                        <input type="number" step={1} className="custom-ranking__inline-number" value={rules[key] ?? 0} onChange={(e) => setOptionalRule(key, Math.round(Number(e.target.value) || 0))} disabled={busy} />
                      ) : null}
                    </span>
                  </div>
                ))}
                <label className="custom-ranking__row">
                  <span>
                    Bono por participación
                    <small className="muted">Por cada torneo en el que juega al menos un partido.</small>
                  </span>
                  <input type="number" min={0} step={1} value={rules.participation_bonus} onChange={(e) => setRule("participation_bonus", e.target.value)} disabled={busy} />
                </label>
                <label className="custom-ranking__row">
                  <span>
                    Bono por invicto
                    <small className="muted">Si gana todos sus partidos del torneo.</small>
                  </span>
                  <input type="number" min={0} step={1} value={rules.undefeated_bonus} onChange={(e) => setRule("undefeated_bonus", e.target.value)} disabled={busy} />
                </label>
              </div>
            </>
          ) : null}

          <h3 className="custom-ranking__subtitle">
            <span className="hint-label">
              {mode === "advanced" ? "8" : "4"} · Categorías
              <FieldHint>Cada categoría tiene su propio ranking (Varonil A, Dobles, 40+…). Las categorías guardadas no se borran para no perder puntos.</FieldHint>
            </span>
          </h3>
          <div className="custom-ranking__categories">
            {categories.map((category, index) => (
              <div key={category.id ?? `new-${index}`} className="custom-ranking__category">
                <input
                  aria-label="Nombre de la categoría"
                  value={category.name}
                  onChange={(e) => updateCategory(index, { name: e.target.value })}
                  placeholder="General"
                  disabled={busy}
                />
                {!category.id ? (
                  <button type="button" className="btn btn--ghost btn--compact btn--tap44" onClick={() => removeCategory(index)} disabled={busy || categories.length === 1}>
                    Quitar
                  </button>
                ) : (
                  <span className="muted">guardada</span>
                )}
              </div>
            ))}
            <button type="button" className="btn btn--ghost" onClick={() => setCategories([...categories, { name: "", best_n: 0 }])} disabled={busy}>
              + Agregar categoría
            </button>
          </div>

          <div className="form__actions">
            <button className="adm-players__btn adm-players__btn--primary" type="submit" disabled={busy}>
              {busy ? "Guardando…" : editingId ? "Guardar cambios" : "Crear ranking"}
            </button>
            {editingId && system ? (
              <button className="btn btn--ghost" type="button" onClick={() => void handleToggleActive()} disabled={busy}>
                {system.is_active ? "Pausar ranking" : "Reactivar ranking"}
              </button>
            ) : null}
          </div>
        </div>
      </form>

      {editingId && system ? (
        <>
          <section className="adm-players__panel adm-rank__panel">
            <div className="adm-rank__panel-head">
              <div>
                <h2 className="adm-rank__h2">Puntos iniciales</h2>
                <p className="adm-rank__sub">
                  Si ya tenías un ranking, súbelo aquí: nombre, correo o celular y sus puntos. Se cruza con los jugadores de la plataforma; cada carga sustituye a la anterior de esa categoría.
                </p>
              </div>
              <label className="custom-ranking__category-pick">
                Categoría
                <select value={selectedCategoryId ?? ""} onChange={(e) => setSelectedCategoryId(Number(e.target.value) || null)} disabled={busy}>
                  {system.categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form">
              <div className="form__actions">
                <button className="btn btn--ghost" type="button" onClick={downloadLayout} disabled={busy}>Descargar layout</button>
                <button className="btn btn--ghost" type="button" onClick={() => fileRef.current?.click()} disabled={busy}>Cargar archivo CSV</button>
                <input ref={fileRef} type="file" accept=".csv,.txt,text/csv" hidden onChange={(e) => void readCsvFile(e)} />
              </div>
              <label>
                CSV ({INITIAL_POINTS_LAYOUT})
                <textarea rows={6} value={initialCsv} onChange={(e) => setInitialCsv(e.target.value)} placeholder={`${INITIAL_POINTS_LAYOUT}\nAna López,ana@correo.mx,5512345678,120\nLuis Pérez,,5598765432,80`} disabled={busy} />
              </label>
              <div className="form__actions">
                <button className="adm-players__btn adm-players__btn--primary" type="button" onClick={() => void handleImportInitial()} disabled={busy || !selectedCategoryId || !initialCsv.trim()}>
                  {busy ? "Cargando…" : "Cargar puntos iniciales"}
                </button>
              </div>
              {unmatched.length > 0 ? (
                <div className="alert alert--info" data-no-toast>
                  <strong>{unmatched.length} renglón(es) sin cruce</strong> (el jugador no está en la plataforma con ese nombre, correo o celular):{" "}
                  {unmatched.slice(0, 15).join(", ")}{unmatched.length > 15 ? "…" : ""}
                </div>
              ) : null}
            </div>
          </section>

          <section className="adm-players__panel adm-rank__panel">
            <div className="adm-rank__panel-head">
              <div>
                <h2 className="adm-rank__h2">Ranking vivo · {selectedCategory?.name ?? ""}</h2>
                <p className="adm-rank__sub">Se actualiza solo con cada resultado. Publícalo cuando quieras que los jugadores lo vean en la página pública.</p>
              </div>
              <div className="form__actions">
                <button className="btn btn--ghost" type="button" onClick={() => void handleRecompute()} disabled={busy}>Recalcular todo</button>
                <button className="adm-players__btn adm-players__btn--primary" type="button" onClick={() => void handlePublish()} disabled={busy || standings.length === 0}>Publicar ranking</button>
              </div>
            </div>
            {standings.length === 0 ? (
              <p className="muted">Sin puntos aún en esta categoría. Carga puntos iniciales o liga un torneo desde su paso 7 y captura resultados.</p>
            ) : (
              <div className="responsive-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>#</th><th>Jugador</th><th>Puntos</th><th>PG-PP</th><th>Dif. sets</th><th>Dif. games</th><th>Torneos</th></tr>
                  </thead>
                  <tbody>
                    {standings.map((row) => (
                      <tr key={row.player_profile_id}>
                        <td>{row.position}</td>
                        <td>{row.display_name}</td>
                        <td><strong>{row.points_total}</strong></td>
                        <td>{row.matches_won ?? 0}-{row.matches_lost ?? 0}</td>
                        <td>{(row.sets_diff ?? 0) > 0 ? `+${row.sets_diff}` : row.sets_diff ?? 0}</td>
                        <td>{(row.games_diff ?? 0) > 0 ? `+${row.games_diff}` : row.games_diff ?? 0}</td>
                        <td>{row.tournaments_counted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="adm-players__panel adm-rank__panel">
            <h2 className="adm-rank__h2">Penalizaciones</h2>
            <p className="adm-rank__sub">Resta puntos a un jugador de {selectedCategory?.name ?? "la categoría"} con motivo (conducta, no presentarse, etc.). Queda en el historial.</p>
            <div className="form">
              <div className="custom-ranking__grid">
                <label>
                  Jugador
                  <select value={penaltyPlayerId} onChange={(e) => setPenaltyPlayerId(e.target.value)} disabled={busy || standings.length === 0}>
                    <option value="">Seleccionar</option>
                    {standings.map((row) => (
                      <option key={row.player_profile_id} value={row.player_profile_id}>{row.display_name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Puntos a restar
                  <input type="number" min={1} step={1} value={penaltyPoints} onChange={(e) => setPenaltyPoints(e.target.value)} disabled={busy} />
                </label>
                <label>
                  Motivo
                  <input value={penaltyReason} onChange={(e) => setPenaltyReason(e.target.value)} placeholder="No se presentó a la final" disabled={busy} />
                </label>
              </div>
              <div className="form__actions">
                <button className="btn btn--ghost" type="button" onClick={() => void handlePenalty()} disabled={busy || !penaltyPlayerId || !penaltyReason.trim()}>
                  Aplicar penalización
                </button>
              </div>
            </div>
          </section>

          <section className="adm-players__panel adm-rank__panel">
            <h2 className="adm-rank__h2">De dónde salió cada punto</h2>
            <p className="adm-rank__sub">Los últimos movimientos de {selectedCategory?.name ?? "la categoría"}.</p>
            {events.length === 0 ? (
              <p className="muted">Todavía no hay movimientos.</p>
            ) : (
              <div className="responsive-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>Cuándo</th><th>Jugador</th><th>Movimiento</th><th>Torneo</th><th>Puntos</th></tr>
                  </thead>
                  <tbody>
                    {events.slice(0, 100).map((item) => (
                      <tr key={item.id}>
                        <td>{formatDateTime(item.created_at)}</td>
                        <td>{item.display_name}</td>
                        <td>{KIND_LABELS[item.kind] ?? item.kind}{item.notes ? <small className="muted"> · {item.notes}</small> : null}</td>
                        <td>{item.tournament_name ?? "—"}</td>
                        <td>{item.points > 0 ? `+${item.points}` : item.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
